<template>
  <FlashNotifications ref="flash" />

  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- Object page header -->
    <div ref="pageHeaderRef" class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6">
      <div class="flex items-center gap-1.5 h-10 text-xs">
        <router-link :to="`/organizations/${org.url}/applications`" data-turbo-prefetch="false"
          title="Back to Applications"
          class="w-6 h-6 -ml-1 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:scale-110 transition-all flex-shrink-0">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
        </router-link>
        <router-link :to="`/organizations/${org.url}/applications`" data-turbo-prefetch="false"
          class="text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 font-medium transition-colors">
          Applications
        </router-link>
        <svg class="w-3 h-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/>
        </svg>
        <span class="text-gray-500 dark:text-gray-400 font-medium truncate max-w-[240px]">{{ application?.alias || application?.name || '...' }}</span>
      </div>

      <div v-if="application" class="pb-4">
        <div class="flex items-start sm:items-center justify-between gap-3 min-w-0">
          <div class="flex items-start sm:items-center gap-3 min-w-0">
            <ItComponentLogo :name="application.name" :logo-url="application.logo_url" size="w-10 h-10" initial-text-size="text-lg" class="flex-shrink-0" />
            <div class="min-w-0">
              <h1 class="text-lg font-bold text-gray-900 dark:text-white truncate">{{ application.alias || application.name }}</h1>
              <p v-if="application.alias" class="text-xs text-gray-400 dark:text-gray-500 truncate -mt-0.5">{{ application.name }}</p>
              <div class="flex items-center gap-1.5 flex-wrap mt-1">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="criticalityBadgeClass(application.criticality)">
                  {{ criticalityLabel(application.criticality) }} criticality
                </span>
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <button type="button" @click="showJson = !showJson" title="View this factsheet's data as JSON"
              class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold border rounded-lg transition-colors"
              :class="showJson
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
              </svg>
              <span class="hidden sm:inline">JSON</span>
            </button>
            <button v-if="!showJson" type="button" @click="sectionPrefs.toggleAll()"
              class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path v-if="sectionPrefs.allCollapsed()" stroke-linecap="round" stroke-linejoin="round" d="M4.5 5.25l7.5 7.5 7.5-7.5m-15 6 7.5 7.5 7.5-7.5"/>
                <path v-else stroke-linecap="round" stroke-linejoin="round" d="M19.5 18.75l-7.5-7.5-7.5 7.5m15-6-7.5-7.5-7.5 7.5"/>
              </svg>
              <span class="hidden sm:inline">{{ sectionPrefs.allCollapsed() ? 'Expand all' : 'Collapse all' }}</span>
            </button>
          </div>
        </div>
      </div>
      <!-- Header skeleton: mirrors the loaded icon + title + badges layout -->
      <div v-else class="flex items-center gap-3 min-w-0 pb-4 animate-pulse">
        <div class="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
        <div class="min-w-0 space-y-2">
          <div class="h-4 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="flex items-center gap-1.5">
            <div class="h-5 w-24 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Page content -->
    <div class="py-6 px-4 sm:px-6">

      <!-- Loading skeleton: mirrors the loaded state's single unified card,
           with one placeholder block per section (Information, Companies &
           Departments, Dependencies) in the same order — and each body
           placeholder respects the saved collapse preference, same as the
           real section does once loaded. -->
      <div v-if="loading" class="animate-pulse bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">

        <!-- Information -->
        <div class="px-5 py-4 flex items-center gap-2">
          <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 transition-transform" :class="sectionPrefs.isCollapsed('information') ? '-rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
          <div class="h-3.5 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
        <div v-show="!sectionPrefs.isCollapsed('information')" class="px-5 py-5">
          <div class="flex items-start gap-4">
            <div class="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
            <div class="min-w-0 flex-1 space-y-3 pt-1">
              <div v-for="i in 5" :key="i" class="flex items-center gap-4">
                <div class="w-24 sm:w-28 h-3 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded" :style="{ width: [ '40%', '65%', '50%', '15%', '20%' ][i - 1] }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Companies & Departments -->
        <div class="px-5 py-4 flex items-center gap-2">
          <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 transition-transform" :class="sectionPrefs.isCollapsed('companies_departments') ? '-rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
          <div class="h-3.5 w-44 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
        <div v-show="!sectionPrefs.isCollapsed('companies_departments')" class="px-5 py-5">
          <div class="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded mb-2"></div>
          <div class="flex flex-wrap gap-1.5">
            <div v-for="i in 3" :key="i" class="h-5 w-20 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
          </div>
        </div>

        <!-- Dependencies -->
        <div class="px-5 py-4 flex items-center gap-2">
          <div class="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <svg class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 transition-transform" :class="sectionPrefs.isCollapsed('dependencies') ? '-rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>
          <div class="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
          <div class="h-3.5 w-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
        <!-- pl-[92px] matches DependenciesSection's own body indent (see its
             comment on why: lines up with Information's field labels above). -->
        <div v-show="!sectionPrefs.isCollapsed('dependencies')" class="pl-[92px] pr-5 py-5 space-y-6">
          <div>
            <div class="h-3 w-24 sm:w-28 bg-gray-100 dark:bg-gray-800 rounded mb-2"></div>
            <div class="rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div class="h-8 bg-gray-50/70 dark:bg-black/10 border-b border-gray-100 dark:border-gray-700"></div>
              <div v-for="i in itDependenciesSkeletonCount" :key="i"
                class="flex items-center gap-4 px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                <div class="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
                <div class="h-3 w-28 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="h-3 w-14 bg-gray-100 dark:bg-gray-800 rounded ml-auto"></div>
                <div class="h-3 w-16 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
                <div class="h-3 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center py-24 sm:py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">{{ error }}</h3>
        <div class="flex items-center gap-3 mt-4">
          <button v-if="!notFound" @click="load" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg text-sm transition-colors">
            Try again
          </button>
          <router-link :to="`/organizations/${org.url}/applications`" data-turbo-prefetch="false"
            class="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
            Back to Applications
          </router-link>
        </div>
      </div>

      <!-- Loaded -->
      <template v-else-if="application">

        <JsonView v-if="showJson" :application="application" />

        <!-- Single unified card: Information, Dependencies, and Companies & Departments
             each own their own state/behavior as separate components, but render as sections
             within one card (divided by a single line) rather than separate cards. -->
        <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <InformationSection :application="application" :organization-id="org.id" :can-edit="canEdit" :sticky-top="headerHeight"
            @updated="onApplicationUpdated" @notify="onNotify" />
          <DependenciesSection :application="application" :organization-id="org.id" :organization-url="org.url" :can-edit="canEdit" :sticky-top="headerHeight"
            @updated="onApplicationUpdated" @notify="onNotify" />
          <CompaniesDepartmentsSection :application="application" :organization-id="org.id" :organization-url="org.url" :can-edit="canEdit" :sticky-top="headerHeight"
            @updated="onApplicationUpdated" @notify="onNotify" />
        </div>
      </template>
    </div>
  </div>

</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { useOrgApplicationsStore } from 'stores/orgApplications.js'
import { useApplicationFactsheetSections } from 'composables/useApplicationFactsheetSections.js'
import {
  getCachedItComponentDependenciesCount, setCachedItComponentDependenciesCount,
} from 'composables/useFactsheetDependenciesCountCache.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import InformationSection from 'controllers/vue_components/organizations/applications/factsheet/InformationSection.vue'
import DependenciesSection from 'controllers/vue_components/organizations/applications/factsheet/DependenciesSection.vue'
import CompaniesDepartmentsSection from 'controllers/vue_components/organizations/applications/factsheet/CompaniesDepartmentsSection.vue'
import JsonView from 'controllers/vue_components/organizations/applications/factsheet/JsonView.vue'
import { criticalityLabel, criticalityBadgeClass } from 'controllers/vue_components/organizations/applications/applicationOptions.js'

const flash = ref(null)
const route = useRoute()
const orgGlobalStore = orgStore()
const store = useOrgApplicationsStore()
const sectionPrefs = useApplicationFactsheetSections()

const org = computed(() => orgGlobalStore.current_org || {})
const applicationId = computed(() => route.params.id)

const application = ref(null)
const loading = ref(true)
const error = ref('')
const notFound = ref(false)
const showJson = ref(false)

// Admins/the owner can edit this factsheet's sections; plain members can
// still expand/collapse and read everything, just not see the Edit
// affordances. Same role source and admin-or-owner rule as the Members page.
const canEdit = computed(() => {
  const membership = orgGlobalStore.user_role_in_org?.users_organizations
  return membership?.role === 'admin' || membership?.org_owner === true
})

// Each section's own heading row sticks just below this page header while
// its body scrolls underneath — headerHeight is measured (not hardcoded)
// since the header's height varies with content (e.g. a long application
// name wrapping on narrow screens).
const pageHeaderRef = ref(null)
const headerHeight = ref(0)
let headerObserver = null

// Read synchronously at setup so the Dependencies skeleton renders at close
// to its real row counts on the very first paint, before fetchOne resolves.
// See docs/skeleton_count_caching.md.
const itDependenciesSkeletonCount = ref(getCachedItComponentDependenciesCount(applicationId.value))

function onApplicationUpdated(updated) {
  application.value = updated
}

function onNotify(message, type) {
  flash.value?.show(message, type)
}

watch(() => application.value && (application.value.alias || application.value.name), (name) => {
  if (name) document.title = `${name} | Application Factsheet`
}, { immediate: true })

async function load() {
  loading.value = true
  error.value = ''
  notFound.value = false

  const res = await store.fetchOne(org.value.id, applicationId.value)

  if (!res.js_error && res.response.success) {
    application.value = res.response.data

    setCachedItComponentDependenciesCount(applicationId.value, (application.value.dependencies || []).length)
  } else {
    notFound.value = res.error?.status === 404
    error.value = notFound.value ? 'Application not found.' : 'Something went wrong loading this application.'
  }

  loading.value = false
}

onMounted(() => {
  load()

  if (pageHeaderRef.value) {
    headerObserver = new ResizeObserver((entries) => {
      headerHeight.value = entries[0].contentRect.height
    })
    headerObserver.observe(pageHeaderRef.value)
  }
})

onUnmounted(() => {
  headerObserver?.disconnect()
})
</script>
